// components/portal/SourceFootfallPanel.tsx
'use client';

import { useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { readCampaignRow, resolveCampaignPropertyId } from '#lib/leasing/campaignRow';
import type { PropertyRuntimeState } from '#types/types';
import { PanelSection, PanelRow } from './panelSection';

const DAY_LABELS: Record<string, [string, string]> = {
  mon: ['Monday', 'Lundi'], tue: ['Tuesday', 'Mardi'], wed: ['Wednesday', 'Mercredi'],
  thu: ['Thursday', 'Jeudi'], fri: ['Friday', 'Vendredi'], sat: ['Saturday', 'Samedi'], sun: ['Sunday', 'Dimanche'],
};

export const SourceFootfallPanel = ({ campaignId, lang }: { campaignId: string; lang: string }) => {
  const { kernel } = useKernel();
  const campaign = useKernelStore(state => readCampaignRow(state.inventory, campaignId));
  const missingCampaign = !campaign;
  const campaignStatus = useKernelStore(state => {
    const entry = state.entityKernelState[`Campaign:${campaignId}`]?.state as { runtime?: { footfallCampaignStatus?: 'loading' | 'failed' } } | undefined;
    return entry?.runtime?.footfallCampaignStatus;
  });
  useEffect(() => {
    if (!missingCampaign) return;
    let cancelled = false;
    kernel.current?.patchEntityRuntimeState('Campaign', campaignId, { footfallCampaignStatus: 'loading' });
    void (async () => {
      try {
        const result = await kernel.current?.syncEntity('Campaign', campaignId);
        if (!cancelled && !result) kernel.current?.patchEntityRuntimeState('Campaign', campaignId, { footfallCampaignStatus: 'failed' });
      } catch {
        if (!cancelled) kernel.current?.patchEntityRuntimeState('Campaign', campaignId, { footfallCampaignStatus: 'failed' });
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, missingCampaign, kernel]);
  const fallback = resolveCampaignPropertyId(campaign);
  const ids = [...new Set((campaign?.propertyIds?.length ? campaign.propertyIds : fallback ? [fallback] : [])
    .filter(id => typeof id === 'string' && id.trim()).map(id => id.trim()))];
  const idsKey = ids.join('\0');
  useEffect(() => {
    const pending = idsKey ? idsKey.split('\0') : [];
    let cancelled = false;
    let cursor = 0;
    pending.forEach(id => kernel.current?.patchEntityRuntimeState('Property', id, { footfallStatus: 'loading' }));
    const worker = async () => {
      while (!cancelled && cursor < pending.length) {
        const id = pending[cursor++]!;
        try {
          // A concurrent fresh sync can replace this request. Retry that cancellation once.
          let result;
          try {
            result = await kernel.current?.syncEntity('Property', id, { requireFresh: true });
          } catch (error) {
            if (cancelled || !(error instanceof Error) || error.name !== 'AbortError') throw error;
            result = await kernel.current?.syncEntity('Property', id, { requireFresh: true });
          }
          if (!cancelled) kernel.current?.patchEntityRuntimeState('Property', id, {
            footfallStatus: result && result.footfall !== undefined ? 'loaded' : 'failed',
          });
        } catch {
          if (!cancelled) kernel.current?.patchEntityRuntimeState('Property', id, { footfallStatus: 'failed' });
        }
      }
    };
    void Promise.all(Array.from({ length: Math.min(12, pending.length) }, worker));
    return () => { cancelled = true; };
  }, [campaignId, idsKey, kernel]);
  return (
    <PanelSection title={lang === 'fr' ? 'Fréquentation' : 'Footfall'}>
      {!ids.length ? <p className="mt-2 text-[#8E8E8E]" style={{ fontSize: 12 }}>
        {missingCampaign ? (campaignStatus === 'failed' ? (lang === 'fr' ? 'Cette recherche est momentanément indisponible.' : 'This run is temporarily unavailable.') : (lang === 'fr' ? 'Chargement…' : 'Loading…')) : (lang === 'fr' ? 'Aucun actif dans cette recherche.' : 'No assets in this run.')}
      </p> : ids.map(id => <AssetFootfall key={`${campaignId}:${id}`} propertyId={id} lang={lang} />)}
    </PanelSection>
  );
};

const AssetFootfall = ({ propertyId, lang }: { propertyId: string; lang: string }) => {
  const property = useKernelStore(state => state.inventory.Property.registry[propertyId]);
  const status = useKernelStore(state => {
    const entry = state.entityKernelState[`Property:${propertyId}`]?.state as { runtime?: PropertyRuntimeState } | undefined;
    return entry?.runtime?.footfallStatus;
  });
  const fr = lang === 'fr';
  const number = (value: number | null | undefined) => value == null ? (fr ? 'Indisponible' : 'Unavailable') : value.toLocaleString(fr ? 'fr-FR' : 'en-GB');
  const saved = (at: string | null) => at ? `${fr ? 'Enregistré le' : 'Recorded'} ${new Date(at).toLocaleDateString(fr ? 'fr-FR' : 'en-GB')}` : undefined;
  const data = status === 'loaded' ? property?.footfall : null;
  return (
    <div className="mt-3" data-footfall-asset={propertyId}>
      <div className="break-words font-medium text-[#ECECEC]" style={{ fontSize: 13 }}>{property?.name ?? propertyId}</div>
      {status === undefined || status === 'loading' ? (
        <p className="mt-2 text-[#8E8E8E]" style={{ fontSize: 12 }}>{fr ? 'Chargement…' : 'Loading…'}</p>
      ) : !data ? (
        <p className="mt-2 text-[#8E8E8E]" style={{ fontSize: 12 }}>{status === 'failed'
          ? (fr ? 'Ces données sont momentanément indisponibles.' : 'These signals are temporarily unavailable.')
          : (fr ? 'Aucune donnée de fréquentation enregistrée.' : 'No activity signals recorded.')}</p>
      ) : <>
        {data.station ? <>
          <p className="mt-3 break-words text-[#B4B4B4]" style={{ fontSize: 12 }}>{data.station.name}</p>
          <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 11 }}>
            {fr ? 'Validations en gare · moyenne par jour de semaine' : 'Station validations · weekday averages'}
            {data.station.period ? ` · ${data.station.period.from} – ${data.station.period.to}` : ` · ${fr ? 'Période indisponible' : 'Period unavailable'}`}
          </p>
          <dl className="mt-2">{data.station.perDay.map(day => <PanelRow key={day.day} label={DAY_LABELS[day.day]?.[fr ? 1 : 0] ?? day.day} value={number(day.entries)} />)}</dl>
          <p className="mt-1 break-words text-[#8E8E8E]" style={{ fontSize: 11 }}>{data.station.source ?? (fr ? 'Source indisponible' : 'Source unavailable')} · {saved(data.station.recordedAt)}</p>
        </> : <dl className="mt-2"><PanelRow label={fr ? 'Validations en gare' : 'Station validations'} value={number(null)} tone="muted" /></dl>}
        {data.annualStations.map(station => <div key={station.name} className="mt-3">
          <div className="break-words text-[#B4B4B4]" style={{ fontSize: 12 }}>{station.name}</div>
          <dl><PanelRow label={fr ? `Voyageurs en gare · ${station.year}` : `Station passengers · ${station.year}`} value={number(station.passengers)} checked={saved(data.baseRecordedAt)} /></dl>
          <p className="break-words text-[#8E8E8E]" style={{ fontSize: 11 }}>{station.source ?? (fr ? 'Source indisponible' : 'Source unavailable')}</p>
        </div>)}
        <dl className="mt-3">
          <PanelRow label={fr ? 'Commerces recensés' : 'Recorded shops'} value={number(data.shopsInside)} />
          <PanelRow label={fr ? 'Avis sur le centre' : 'Centre reviews'} value={number(data.centreReviews)} />
        </dl>
        {data.topShops.length ? <>
          <div className="mt-2 text-[#8E8E8E]" style={{ fontSize: 11 }}>{fr ? 'Commerces · avis Google' : 'Shops · Google reviews'}</div>
          <dl>{data.topShops.map(shop => <PanelRow key={shop.name} label={shop.name} labelTitle={shop.name} value={number(shop.reviews)} />)}</dl>
        </> : null}
        <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 11 }}>{saved(data.baseRecordedAt)}</p>
        <p className="mt-3 text-[#8E8E8E]" style={{ fontSize: 11 }}>{fr
          ? 'La fréquentation de la gare et les avis ne mesurent pas les visiteurs du centre, leur profil ou leur parcours entre les boutiques.'
          : 'Station traffic and reviews do not measure centre visitors, visitor profiles or movement between shops.'}</p>
      </>}
    </div>
  );
};
